import { FormField } from '@workday/canvas-kit-react/form-field';
import { autoId, formFieldErrorPropsV2 } from '@workday/upc-common';
import { TextInput } from '@workday/canvas-kit-react/text-input';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateNameMutation } from '../../../../slices/account-api.slice';
import { useTranslation } from 'react-i18next';

interface ProfileNameSettingsRowFormState {
    firstName: string;
    lastName: string;
}

interface ProfileNameModalContentProps {
    onClose: () => void;
    userInfo: {
        firstName: string;
        lastName: string;
    };
}

export function ProfileNameModalContent({ onClose, userInfo }: ProfileNameModalContentProps) {
    const [updateName, { isSuccess }] = useUpdateNameMutation();

    const { t } = useTranslation('account');

    useEffect(() => {
        if (isSuccess) {
            onClose();
        }
    }, [onClose, isSuccess]);

    const onSubmit = values => {
        updateName(values);
    };

    const { register, handleSubmit, formState } = useForm<ProfileNameSettingsRowFormState>();

    return (
        <div className='settingsModalContent'>
            <form onSubmit={handleSubmit(onSubmit)}>
                <h1>{t('ProfileNameModalContent.title')}</h1>
                <h2>{t('ProfileNameModalContent.description')}</h2>

                <div className='settingsModalContent__body'>
                    <FormField
                        label={t('ProfileNameModalContent.firstNameLabel')}
                        grow={true}
                        {...formFieldErrorPropsV2(
                            formState.errors.firstName,
                            undefined,
                            t('Required.fieldErrorLabel', { ns: 'shared' })
                        )}>
                        <TextInput
                            {...autoId('firstNameField')}
                            {...register('firstName', {
                                required: t('Required.fieldGeneric', { ns: 'shared' }),
                                minLength: {
                                    value: 2,
                                    message: t('Required.fieldMinLengthError', { ns: 'shared' })
                                }
                            })}
                            defaultValue={userInfo.firstName}
                            autoFocus={true}
                            autoComplete='off'
                        />
                    </FormField>

                    <FormField
                        label={t('ProfileNameModalContent.lastNameLabel')}
                        grow={true}
                        {...formFieldErrorPropsV2(
                            formState.errors.lastName,
                            undefined,
                            t('Required.fieldErrorLabel', { ns: 'shared' })
                        )}>
                        <TextInput
                            {...autoId('lastNameField')}
                            {...register('lastName', {
                                required: t('Required.fieldGeneric', { ns: 'shared' }),
                                minLength: {
                                    value: 2,
                                    message: t('Required.fieldMinLengthError', { ns: 'shared' })
                                }
                            })}
                            defaultValue={userInfo.lastName}
                            autoComplete='off'
                        />
                    </FormField>
                </div>

                <div className='settingsModalContent__footer'>
                    <PrimaryButton
                        type='submit'
                        {...autoId('submitButton')}
                        className='settingsModalContent__submit'
                        size='large'
                        grow={true}>
                        {t('ProfileNameModalContent.submitButton')}
                    </PrimaryButton>
                    <div className='settingsModalContent__cancel'>
                        <TertiaryButton {...autoId('cancelButton')} type='button' size='large' onClick={onClose}>
                            {t('ProfileNameModalContent.cancelButton')}
                        </TertiaryButton>
                    </div>
                </div>
            </form>
        </div>
    );
}
